import { RouterLink } from "@/components/RouterLink";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ExternalLink, LucideIcon } from "lucide-react";
import React from "react";

export type AlertBannerVariant = "warning" | "danger" | "info";

export interface AlertBannerAction {
  type: "button" | "link";
  text: string;
  onClick?: () => void;
  href?: string;
  variant?:
    | "default"
    | "outline"
    | "destructive"
    | "secondary"
    | "ghost"
    | "link";
}

export interface AlertBannerProps {
  variant: AlertBannerVariant;
  icon?: LucideIcon;
  children: React.ReactNode;
  action?: AlertBannerAction;
  className?: string;
}

const getVariantStyles = (variant: AlertBannerVariant) => {
  switch (variant) {
    case "warning":
      return {
        alert: "border-2 border-lc-warning/30 bg-lc-warning/10",
        icon: "!text-lc-warning fill-background",
        text: "text-lc-warning",
        button: "border-lc-warning/30 text-lc-warning hover:bg-lc-warning/15",
      };
    case "info":
      return {
        alert: "border-2 border-lc-neutral/30 bg-lc-neutral/10",
        icon: "!text-lc-neutral fill-background",
        text: "text-lc-neutral",
        button: "border-lc-neutral/30 text-lc-neutral hover:bg-lc-neutral/15",
      };
    default:
      return getVariantStyles("info");
  }
};

export const AlertBanner: React.FC<AlertBannerProps> = ({
  variant,
  icon: Icon,
  children,
  action,
  className = "",
}) => {
  const styles = getVariantStyles(variant);

  return (
    <Alert
      className={`${styles.alert} relative ${variant === "warning" ? "pb-3" : ""} ${className}`}
    >
      {Icon && (
        <Icon
          className={`absolute size-8 !-left-3 !-top-3 ${styles.icon} overflow-hidden`}
        />
      )}
      <AlertDescription
        className={`${variant === "danger" ? "text-sm" : "text-base"} ${styles.text} h-full ${Icon ? "!pl-2" : ""}`}
      >
        <div className="flex gap-2 text-left flex-col lg:flex-row lg:justify-between">
          <span>{children}</span>
          {action && action.type === "button" && (
            <Button
              variant={action.variant || "outline"}
              className={`${styles.button} w-full lg:w-auto`}
              onClick={action.onClick}
              size="sm"
            >
              {action.text}
            </Button>
          )}
          {action && action.type === "link" && action.href && (
            <RouterLink to={action.href} className="whitespace-nowrap">
              {action.text}
              <ExternalLink className="relative bottom-[1px] inline w-3.5 h-3.5" />
            </RouterLink>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default AlertBanner;
